import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ChevronRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { DashboardMock, PhoneFrame } from "@/components/mockups/PhoneMockup";

const fade = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { t } = useI18n();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-28 overflow-hidden"
    >
      {/* background grid */}
      <div className="absolute inset-0 grid-bg mask-fade-b pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 0%, rgba(0,255,136,0.10), transparent 70%)",
        }}
      />

      <div className="container-px mx-auto max-w-7xl relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          {/* Text */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <motion.div
              initial="hidden"
              animate="show"
              variants={fade}
              custom={0}
              className="inline-flex items-center gap-2 rounded-full hairline px-3 py-1.5"
              style={{ background: "rgba(28,31,38,0.6)" }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-glow" />
              <span className="label-tag text-[10px]">{t.hero.tag}</span>
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="show"
              variants={fade}
              custom={1}
              className="font-display font-bold tracking-tight text-ink mt-6 text-balance leading-[0.95]
                         text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px]"
              data-testid="hero-title"
            >
              {t.hero.title1}{" "}
              <br className="hidden sm:block" />
              <span className="neon-text">{t.hero.title2}</span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              variants={fade}
              custom={2}
              className="mt-6 text-ink/65 text-base sm:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fade}
              custom={3}
              className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 justify-center lg:justify-start"
            >
              <a href="#cta" className="btn-neon" data-testid="hero-cta-primary">
                {t.hero.ctaPrimary}
                <ChevronRight size={14} />
              </a>
              <a href="#how" className="btn-ghost" data-testid="hero-cta-secondary">
                {t.hero.ctaSecondary}
                <ArrowDown size={14} />
              </a>
            </motion.div>

            {/* stats */}
            <motion.dl
              initial="hidden"
              animate="show"
              variants={fade}
              custom={4}
              className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0"
              data-testid="hero-stats"
            >
              {[
                { v: "12,847", l: t.hero.stats.users },
                { v: "+47", l: t.hero.stats.focus },
                { v: "23d", l: t.hero.stats.streak },
              ].map((s, i) => (
                <div key={i} className="border-l pl-3" style={{ borderColor: "rgba(245,247,250,0.10)" }}>
                  <dd className="mono-num text-2xl text-ink">{s.v}</dd>
                  <dt className="label-tag text-[10px] mt-1">{s.l}</dt>
                </div>
              ))}
            </motion.dl>
          </div>

          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative flex justify-center lg:justify-end"
          >
            {/* corner ticks frame around the mockup */}
            <div className="relative">
              <div className="absolute -inset-6 hairline rounded-[60px] hidden md:block opacity-60" />
              <div className="corner-tick tl !-top-7 !-left-7" />
              <div className="corner-tick tr !-top-7 !-right-7" />
              <div className="corner-tick bl !-bottom-7 !-left-7" />
              <div className="corner-tick br !-bottom-7 !-right-7" />
              <PhoneFrame>
                <DashboardMock t={t.hero.mockup} />
              </PhoneFrame>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
